import type { KullaniciDurum, KullaniciRol } from "../mocks/kullanicilar";

export interface ApiCustomer {
  id: number;
  ad_soyad: string;
  telefon: string;
  kisa_not?: string | null;
  toplam_hisse_sayisi: number;
  toplam_borc: number;
  toplam_odenen: number;
  created_at: string;
  updated_at: string;
}

export interface ApiUser {
  id: number;
  ad_soyad: string;
  telefon: string;
  email?: string | null;
  username?: string | null;
  rol: KullaniciRol;
  durum: KullaniciDurum;
  customer_id?: number | null;
  force_password_change: boolean;
  password_changed_at?: string | null;
  son_giris?: string | null;
  sms_operational_opt_in?: boolean | null;
  sms_operational_opt_in_at?: string | null;
  sms_marketing_opt_in?: boolean | null;
  sms_marketing_opt_in_at?: string | null;
  created_at: string;
}

export interface LoginResponse {
  access_token: string;
  token_type: string;
  user: ApiUser;
}

export interface ChangePasswordPayload {
  current_password: string;
  new_password: string;
  sms_operational_opt_in?: boolean | null;
  sms_marketing_opt_in?: boolean | null;
}

export interface CreateUserPayload {
  ad_soyad: string;
  telefon: string;
  email?: string | null;
  username?: string | null;
  rol: KullaniciRol;
  durum: KullaniciDurum;
  customer_id?: number | null;
  force_password_change: boolean;
  sms_operational_opt_in?: boolean | null;
  sms_marketing_opt_in?: boolean | null;
  sifre: string;
}

export interface UpdateUserPayload {
  ad_soyad?: string;
  telefon?: string;
  email?: string | null;
  username?: string | null;
  rol?: KullaniciRol;
  durum?: KullaniciDurum;
  customer_id?: number | null;
  force_password_change?: boolean;
  sms_operational_opt_in?: boolean | null;
  sms_marketing_opt_in?: boolean | null;
}



export interface PaymentImportPreviewRow {
  row_number: number;
  payment_date: string;
  amount: number;
  payment_type: "Nakit" | "Havale";
  sender_name: string;
  phone: string;
  fis_no: string;
  kupe_no: string;
  siparis_no: string;
  hisse_no: string;
  description: string;
  reference_no: string;
  matched_customer_id?: number | null;
  matched_customer_name?: string;
  matched_phone?: string;
  matched_share_ids: number[];
  matched_open_total: number;
  confidence: "high" | "medium" | "low" | "none";
  match_reason: string;
  issues: string[];
  can_apply: boolean;
  raw: Record<string, unknown>;
}

export interface PaymentImportPreviewResponse {
  filename: string;
  summary: {
    total_rows: number;
    high_confidence: number;
    medium_confidence: number;
    low_confidence: number;
    ready_to_apply: number;
    unmatched: number;
  };
  rows: PaymentImportPreviewRow[];
  supported_headers: string[];
}

export interface PaymentImportApplyItem {
  row_number: number;
  matched_customer_id?: number | null;
  matched_share_ids: number[];
  payment_date: string;
  amount: number;
  payment_type: "Nakit" | "Havale";
  description?: string | null;
  fis_no?: string | null;
  reference_no?: string | null;
  sender_name?: string | null;
}

export interface PaymentImportApplyResponse {
  ok: true;
  imported_rows: number;
  imported_amount: number;
  affected_customer_ids: number[];
}

export interface BankTransferPreviewRow {
  row_number: number;
  tarih: string;
  fis_no: string;
  aciklama: string;
  tutar: number;
  issues: string[];
  raw: Record<string, unknown>;
}

export interface BankTransferPreviewResponse {
  filename: string;
  rows: BankTransferPreviewRow[];
  summary: {
    total_rows: number;
    valid_rows: number;
    invalid_rows: number;
  };
}
export interface ResetUserPasswordPayload {
  new_password: string;
}

export interface ResetOperatorDefaultPasswordResponse {
  ok: true;
  message: string;
  default_password: string;
  username: string;
}

export interface CreateCustomerPayload {
  ad_soyad: string;
  telefon: string;
  kisa_not?: string | null;
}

export interface UpdateCustomerPayload {
  ad_soyad?: string;
  telefon?: string;
  kisa_not?: string | null;
  toplam_hisse_sayisi?: number;
  toplam_borc?: number;
  toplam_odenen?: number;
}

const API_PREFIX = "/api";
const TOKEN_KEY = "evren_token";

export class ApiRequestError extends Error {
  status: number;
  detail?: string;

  constructor(status: number, detail: string) {
    super(detail);
    this.name = "ApiRequestError";
    this.status = status;
    this.detail = detail;
  }
}

export function isApiRequestError(error: unknown): error is ApiRequestError {
  return error instanceof ApiRequestError;
}

export function isAuthError(error: unknown) {
  return (
    isApiRequestError(error) &&
    (error.status === 401 || error.status === 403)
  );
}

function readToken() {
  if (typeof window === "undefined") return "";
  return window.localStorage.getItem(TOKEN_KEY) ?? "";
}

function buildUrl(path: string) {
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  return `${API_PREFIX}${path.startsWith("/") ? path : `/${path}`}`;
}

async function parseError(response: Response): Promise<string> {
  try {
    const data = await response.json();
    if (typeof data?.detail === "string") return data.detail;
  } catch {
    // ignore
  }

  return `İstek başarısız (${response.status})`;
}

export async function apiFetch<T>(
  path: string,
  init: RequestInit = {}
): Promise<T> {
  const token = readToken();

  const headers = new Headers(init.headers ?? {});
  if (!headers.has("Content-Type") && init.body) {
    headers.set("Content-Type", "application/json");
  }
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  let response: Response;

  try {
    response = await fetch(buildUrl(path), {
      ...init,
      headers,
    });
  } catch {
    throw new ApiRequestError(0, "Sunucuya ulaşılamadı.");
  }

  if (!response.ok) {
    throw new ApiRequestError(response.status, await parseError(response));
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

async function apiFetchWithHeaders<T>(
  path: string,
  init: RequestInit = {}
): Promise<{ data: T; headers: Headers }> {
  const token = readToken();

  const headers = new Headers(init.headers ?? {});
  if (!headers.has("Content-Type") && init.body) {
    headers.set("Content-Type", "application/json");
  }
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  let response: Response;

  try {
    response = await fetch(buildUrl(path), {
      ...init,
      headers,
    });
  } catch {
    throw new ApiRequestError(0, "Sunucuya ulaşılamadı.");
  }

  if (!response.ok) {
    throw new ApiRequestError(response.status, await parseError(response));
  }

  if (response.status === 204) {
    return { data: undefined as T, headers: response.headers };
  }

  return { data: (await response.json()) as T, headers: response.headers };
}

async function apiFormFetch<T>(path: string, formData: FormData): Promise<T> {
  const token = readToken();
  const headers = new Headers();
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  let response: Response;

  try {
    response = await fetch(buildUrl(path), {
      method: "POST",
      headers,
      body: formData,
    });
  } catch {
    throw new ApiRequestError(0, "Sunucuya ulaşılamadı.");
  }

  if (!response.ok) {
    throw new ApiRequestError(response.status, await parseError(response));
  }

  return (await response.json()) as T;
}
export function writeApiToken(token: string) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(TOKEN_KEY, token);
}

export function clearApiToken() {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(TOKEN_KEY);
}

export async function loginRequest(telefon: string, sifre: string) {
  return apiFetch<LoginResponse>("/auth/login", {
    method: "POST",
    body: JSON.stringify({ telefon, sifre }),
  });
}

export async function meRequest() {
  return apiFetch<ApiUser>("/auth/me");
}

export async function changePasswordRequest(payload: ChangePasswordPayload) {
  return apiFetch<ApiUser>("/auth/change-password", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function listUsersRequest() {
  return apiFetch<ApiUser[]>("/users");
}

export async function createUserRequest(payload: CreateUserPayload) {
  return apiFetch<ApiUser>("/users", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateUserRequest(
  userId: number,
  payload: UpdateUserPayload
) {
  return apiFetch<ApiUser>(`/users/${userId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function resetUserPasswordRequest(
  userId: number,
  payload: ResetUserPasswordPayload
) {
  return apiFetch<{ ok: true; message: string }>(
    `/users/${userId}/reset-password`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    }
  );
}

export async function resetOperatorDefaultPasswordRequest(userId: number) {
  return apiFetch<ResetOperatorDefaultPasswordResponse>(
    `/users/${userId}/reset-operator-default-password`,
    { method: "POST" }
  );
}

export async function deleteUserRequest(userId: number) {
  return apiFetch<{ ok: true; message: string }>(`/users/${userId}`, {
    method: "DELETE",
  });
}

export async function listCustomersRequest() {
  return apiFetch<ApiCustomer[]>("/customers");
}

export async function createCustomerRequest(payload: CreateCustomerPayload) {
  return apiFetch<ApiCustomer>("/customers", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateCustomerRequest(
  customerId: number,
  payload: UpdateCustomerPayload
) {
  return apiFetch<ApiCustomer>(`/customers/${customerId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function getStateRequest<T>(key: string) {
  return apiFetch<T>(`/state/${key}`);
}

export async function getStateWithRevisionRequest<T>(key: string) {
  const response = await apiFetchWithHeaders<T>(`/state/${key}`);
  return {
    data: response.data,
    revision: Number(response.headers.get("X-State-Revision") ?? "1") || 1,
  };
}

export async function putStateRequest<T>(key: string, payload: T) {
  return apiFetch<T>(`/state/${key}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export async function putStateWithRevisionRequest<T>(
  key: string,
  payload: T,
  revision: number
) {
  const response = await apiFetchWithHeaders<T>(`/state/${key}`, {
    method: "PUT",
    headers: { "X-State-Revision": String(revision) },
    body: JSON.stringify(payload),
  });
  return {
    data: response.data,
    revision: Number(response.headers.get("X-State-Revision") ?? revision + 1) || revision + 1,
  };
}


export interface OperationResult {
  ok: true;
  animals: unknown[];
  payments: unknown[];
  bankTransfers?: unknown[];
  deliveries?: unknown[];
  createdShareId?: number;
  deletedShareId?: number;
  completedAnimalIds?: number[];
  releasedReceipts?: string[];
  sharePresales?: unknown;
  createdPresaleId?: number;
  linkedShareId?: number;
  linkedShareIds?: number[];
}

export async function createSaleOperationRequest(payload: Record<string, unknown>) {
  return apiFetch<OperationResult>("/ops/sale/create", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function createPaymentOperationRequest(payload: Record<string, unknown>) {
  return apiFetch<OperationResult>("/ops/payment/create", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function deleteSaleOperationRequest(shareId: number) {
  return apiFetch<OperationResult>("/ops/sale/delete", {
    method: "POST",
    body: JSON.stringify({ shareId }),
  });
}



export async function createSharePresaleOperationRequest(payload: Record<string, unknown>) {
  return apiFetch<OperationResult>("/ops/share-presale/create", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateSharePresaleOperationRequest(payload: Record<string, unknown>) {
  return apiFetch<OperationResult>("/ops/share-presale/update", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function linkSharePresalesToAnimalBatchOperationRequest(payload: Record<string, unknown>) {
  return apiFetch<OperationResult>("/ops/share-presale/link-animal-batch", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function linkSharePresaleToAnimalOperationRequest(payload: Record<string, unknown>) {
  return apiFetch<OperationResult>("/ops/share-presale/link-animal", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function unlinkSharePresaleFromAnimalOperationRequest(saleId: number) {
  return apiFetch<OperationResult>("/ops/share-presale/unlink-animal", {
    method: "POST",
    body: JSON.stringify({ saleId }),
  });
}

export async function deleteSharePresaleOperationRequest(saleId: number) {
  return apiFetch<OperationResult>("/ops/share-presale/delete", {
    method: "POST",
    body: JSON.stringify({ saleId }),
  });
}

export async function previewPaymentImportRequest(file: File) {
  const formData = new FormData();
  formData.append("file", file);
  return apiFormFetch<PaymentImportPreviewResponse>("/payment-import/preview", formData);
}

export async function applyPaymentImportRequest(payload: { items: PaymentImportApplyItem[] }) {
  return apiFetch<PaymentImportApplyResponse>("/payment-import/apply", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function previewBankTransferImportRequest(file: File) {
  const formData = new FormData();
  formData.append("file", file);
  return apiFormFetch<BankTransferPreviewResponse>("/bank-transfers/preview", formData);
}
